import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  Clock,
  Coins,
  FileText,
  Handshake,
  Percent,
  PieChart as PieChartIcon,
  Target,
  TrendingUp,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
// Renommé pour éviter la collision avec le Tooltip de Recharts (déjà importé).
import { Tooltip as UITooltip } from '@/components/ui/Tooltip';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/StatusBadge';
import { DateRangeBadges, DateRangeInputs, type DateRange } from '@/components/ui/DateRangePicker';
import { PERIOD_PRESETS, defaultPeriodRange, type PeriodPresetKey } from '@/lib/date-presets';
import {
  AXIS_STYLE,
  bucketToIsoRange,
  CHART_COLORS,
  ChartTooltip,
  disambiguateBucketLabels,
  formatEuroCompact,
  GRID_STYLE,
} from '@/components/ui/chart';
import { useAuthStore } from '@/stores/auth';
import { useDebouncedRealtimeRefresh, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import {
  computeGranularity,
  kpiApi,
  STAGE_LABELS,
  type DashboardResponse,
  type FunnelResponse,
  type KpiFilters,
} from '@/services/kpi.api';
import { formatRelative } from '@/components/TimelineSection';
import {
  objectivesApi,
  OBJECTIVE_PERIOD_LABELS,
  OBJECTIVE_TYPE_LABELS,
  type ObjectiveItem,
  type ObjectiveProgress,
} from '@/services/objectives.api';
import { cn } from '@/lib/utils';
import { getAccentHex } from '@/lib/accent';

// Presets temporels + bornes « Cette année » : single source of truth dans
// `@/lib/date-presets` (partagé avec le Pipeline). Cf décision 2026-06-27.

// Stages non terminales - utilisé pour le drill-down "Opportunités en cours"
// et "CA prévisionnel" (cohérent avec NON_TERMINAL_STAGES côté backend).
// Refonte 2026-05-21 : nouveau pipeline LBC à 7 stages non-terminaux.
const ACTIVE_STAGES_CSV =
  'suspect,contacted,meeting_booked,meeting_done,awaiting_client_docs,awaiting_quote_supplier,quote_sent';

/** Ouvre la page Pipeline en vue liste, dans un nouvel onglet, avec les
 *  filtres passés en query string. Utilisé par les onClick des graphes
 *  et des StatCards. Aligné avec le helper `openInPipeline` de KpiDirectionPage. */
function openInPipeline(params: Record<string, string | undefined>): void {
  const qs = new URLSearchParams({ view: 'list' });
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.set(k, v);
  }
  window.open(`/pipeline?${qs.toString()}`, '_blank', 'noopener,noreferrer');
}

export function DashboardPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRange>(defaultPeriodRange);
  const [activePreset, setActivePreset] = useState<PeriodPresetKey | null>('year');
  // Pivot de date (décision produit 2026-07-03, demande client) : 'created'
  // (défaut, comportement historique) ou 'effective' (toggle « Date d'effet » :
  // toutes les cards hors Activité récente filtrent sur la date d'effet).
  const [datePivot, setDatePivot] = useState<'created' | 'effective'>('created');
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [funnel, setFunnel] = useState<FunnelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Granularité adaptative dérivée de la plage : mois / semaine / jour.
  // Envoyée au backend qui pilote le `date_trunc(...)`. Le frontend l'utilise
  // aussi pour formater les labels X axis des graphs temporels.
  const granularity = computeGranularity(range.from, range.to);
  const filters: KpiFilters = {
    period_start: range.from,
    period_end: range.to,
    granularity,
    date_pivot: datePivot,
  };

  async function load() {
    setError(null);
    try {
      const [d, f] = await Promise.all([
        kpiApi.dashboard(token, filters),
        kpiApi.funnel(token, filters),
      ]);
      setDashboard(d);
      setFunnel(f);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, datePivot]);

  // Realtime : refresh global debouncé 2s pour ne pas spammer les queries
  // d'agrégation (dashboard + funnel) quand beaucoup de mutations arrivent
  // en burst (ex: import CSV 100 prospects → 100 events → 1 seul refetch).
  useDebouncedRealtimeRefresh(
    [
      REALTIME_EVENTS.OPPORTUNITY_UPDATED,
      REALTIME_EVENTS.PROSPECT_UPDATED,
      REALTIME_EVENTS.TASK_MUTATED,
    ],
    () => void load(),
    2000,
  );

  const period = { period_start: range.from, period_end: range.to };

  return (
    <AppLayout>
      <PageHeader
        title={`Bonjour ${user?.firstName ?? ''}`}
        subtitle="Synthèse de votre activité commerciale."
        // Inputs date sur la ligne du titre, badges juste en-dessous : visuel-
        // lement les badges se retrouvent à hauteur du sous-titre (`items-end`
        // empile les deux blocs à droite avec un petit gap).
        actions={
          <div className="flex flex-col items-end gap-1.5">
            <DateRangeInputs
              value={range}
              onChange={(next, key) => {
                setRange(next);
                setActivePreset(key);
              }}
            />
            {/* Ligne des badges de période + toggle pivot (tout à droite des
                badges, sur la même ligne). Les inputs restent au-dessus. */}
            <div className="flex items-center gap-3">
              <DateRangeBadges
                activePresetKey={activePreset}
                presets={PERIOD_PRESETS}
                onChange={(next, key) => {
                  setRange(next);
                  setActivePreset(key);
                }}
                align="end"
              />
              <DatePivotToggle value={datePivot} onChange={setDatePivot} />
            </div>
          </div>
        }
      />

      {error && <Banner tone="danger">{error}</Banner>}
      {!dashboard && !error && <DashboardSkeleton />}

      {dashboard && funnel && (
        <>
          <StatsRow data={dashboard} period={period} />
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <RevenueLineCard data={dashboard.revenueByMonth} granularity={granularity} />
            <FunnelCard
              data={funnel}
              period={period}
              avgConversionDays={dashboard.alerts.avgConversionDays}
            />
          </div>
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <ContractTypeCard data={dashboard.contractTypeBreakdown} period={period} />
            <ActivityCard data={dashboard.activityByWeek} />
          </div>
          <AlertsSection
            data={dashboard.alerts}
            onOpenOpportunity={(id) => navigate(`/opportunities/${id}`)}
          />
          <ObjectivesWidget />
        </>
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Toggle « Date de création / Date d'effet » (décision produit 2026-07-03,
// demande client). Segmented control Stripe-like, même design que
// RevenueModeToggle. « Date d'effet » repivote TOUTES les cards pilotées par
// la période sur `effective_date` (hors Activité récente + cards d'alertes
// temps réel). Volontairement incohérent — pédagogique.
// ---------------------------------------------------------------------------

function DatePivotToggle({
  value,
  onChange,
}: {
  value: 'created' | 'effective';
  onChange: (v: 'created' | 'effective') => void;
}) {
  const opts: Array<{ value: 'created' | 'effective'; label: string; title: string }> = [
    { value: 'created', label: 'Date de création', title: 'Filtre historique (chaque card garde son pivot propre)' },
    {
      value: 'effective',
      label: "Date d'effet",
      title: "Filtre toutes les cards (hors Activité récente) sur la date d'effet du contrat",
    },
  ];
  return (
    <div className="inline-flex rounded-md border border-border-subtle bg-gray-50 p-[2px] text-[11px]">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          title={o.title}
          className={cn(
            'rounded-[5px] px-2.5 py-1 font-medium leading-tight transition-colors',
            value === o.value ? 'bg-white text-brand shadow-sm' : 'text-text-muted hover:text-brand',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats row : 5 StatCards avec variation
// ---------------------------------------------------------------------------

function StatsRow({
  data,
  period,
}: {
  data: DashboardResponse;
  period: { period_start: string; period_end: string };
}) {
  const { stats } = data;
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatCard
        icon={<Target className="h-4 w-4" />}
        label="Opportunités en cours"
        value={stats.ongoingOpportunities.value.toLocaleString('fr-FR')}
        delta={deltaProp(stats.ongoingOpportunities.deltaPct)}
        deltaPosition="below"
        // Backend = snapshot non-terminales créées ≤ period.end.
        onValueClick={() =>
          openInPipeline({ stages: ACTIVE_STAGES_CSV, created_before: period.period_end })
        }
      />
      <ForecastRevenueCard stats={stats} period={period} />
      <StatCard
        icon={<FileText className="h-4 w-4" />}
        label="Devis émis"
        value={stats.quotesEmittedInPeriod.value.toLocaleString('fr-FR')}
        delta={deltaProp(stats.quotesEmittedInPeriod.deltaPct)}
        deltaPosition="below"
        // Backend = opps passées à quote_sent/won/lost via stage_changed_at dans la période.
        onValueClick={() =>
          openInPipeline({
            stages: 'quote_sent,won,lost',
            stage_changed_after: period.period_start,
            stage_changed_before: period.period_end,
          })
        }
      />
      <StatCard
        icon={<Handshake className="h-4 w-4" />}
        label="Signés"
        value={stats.signedInPeriod.value.toLocaleString('fr-FR')}
        delta={deltaProp(stats.signedInPeriod.deltaPct)}
        deltaPosition="below"
        // Backend = stage=won AND signed_at dans la période.
        onValueClick={() =>
          openInPipeline({
            stage: 'won',
            signed_after: period.period_start,
            signed_before: period.period_end,
          })
        }
      />
      <StatCard
        icon={<Percent className="h-4 w-4" />}
        label="Taux de conversion"
        value={
          // Tooltip explicite la sémantique cohorte (alignée avec le Funnel
          // depuis 2026-04-27) : opps créées dans la période ET signées,
          // divisé par toutes les opps créées dans la période.
          <UITooltip
            content="Opps créées et signées / opps créées dans la période"
            side="top"
            align="start"
          >
            <span>{`${stats.conversionRatePct.value} %`}</span>
          </UITooltip>
        }
        delta={deltaProp(stats.conversionRatePct.deltaPct)}
        deltaPosition="below"
        // Pas de drill-down - un ratio n'est pas filtrable en liste.
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card « CA prévisionnel » avec toggle « Brut » (décision 2026-06-27).
// Toggle au repos (défaut) → CA pondéré par le score (weightedRevenueTotal).
// Toggle actif « Brut » → CA brut non pondéré (unweightedRevenueTotal). Le
// toggle est posé en bout de la ligne du badge de variation (pas de ligne
// dédiée). Choix persisté en localStorage ; les deux valeurs viennent du
// backend → bascule instantanée sans refetch.
// ---------------------------------------------------------------------------

const FORECAST_WEIGHTED_KEY = 'crm-dashboard-forecast-weighted';

function readForecastWeighted(): boolean {
  try {
    // Toute valeur ≠ '0' (y compris absence) → pondération active par défaut.
    return localStorage.getItem(FORECAST_WEIGHTED_KEY) !== '0';
  } catch {
    return true;
  }
}

function ForecastRevenueCard({
  stats,
  period,
}: {
  stats: DashboardResponse['stats'];
  period: { period_start: string; period_end: string };
}) {
  const [weighted, setWeighted] = useState<boolean>(readForecastWeighted);
  const stat = weighted ? stats.weightedRevenueTotal : stats.unweightedRevenueTotal;

  function toggle() {
    setWeighted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(FORECAST_WEIGHTED_KEY, next ? '1' : '0');
      } catch {
        /* localStorage indisponible : on conserve l'état en mémoire */
      }
      return next;
    });
  }

  return (
    <StatCard
      icon={<TrendingUp className="h-4 w-4" />}
      label="CA prévisionnel"
      value={`${Math.round(stat.value).toLocaleString('fr-FR')} €`}
      delta={deltaProp(stat.deltaPct)}
      deltaPosition="below"
      // Drill-down : opps non terminales créées ≤ fin de période. En mode
      // pondéré on ne garde que les opps à contribution CA pondérée > 0
      // (cohérent avec la valeur affichée) ; en mode brut on ouvre tout le pipe.
      onValueClick={() =>
        openInPipeline({
          stages: ACTIVE_STAGES_CSV,
          created_before: period.period_end,
          ...(weighted ? { min_weighted_revenue: '0.01' } : {}),
        })
      }
      // Toggle « Brut » placé en bout de la ligne du badge de variation (pas de
      // ligne dédiée). Switch ACTIF (accent) = mode brut (non pondéré) ; au repos
      // = pondéré (défaut). Le label reste « Brut », le switch indique s'il est actif.
      deltaTrailing={
        <button
          type="button"
          role="switch"
          aria-checked={!weighted}
          aria-label="Afficher le CA brut (non pondéré par le score)"
          onClick={toggle}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20"
        >
          <span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
            Brut
          </span>
          <span
            className={cn(
              'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors',
              !weighted ? 'bg-accent' : 'bg-gray-300',
            )}
          >
            <span
              className={cn(
                'inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform',
                !weighted ? 'translate-x-3.5' : 'translate-x-0.5',
              )}
            />
          </span>
        </button>
      }
    />
  );
}

function deltaProp(pct: number | null): StatsDelta {
  if (pct === null) return { value: 'new', direction: 'new' };
  if (pct === 0) return { value: '0 %', direction: 'neutral' };
  return {
    value: `${pct > 0 ? '+' : ''}${pct} %`,
    direction: pct > 0 ? 'up' : 'down',
  };
}

interface StatsDelta {
  value: string;
  direction: 'up' | 'down' | 'neutral' | 'new';
}

// ---------------------------------------------------------------------------
// Funnel - barres horizontales
// ---------------------------------------------------------------------------

function FunnelCard({
  data,
  period,
  avgConversionDays,
}: {
  data: FunnelResponse;
  period: { period_start: string; period_end: string };
  /** Délai moyen création→signature sur les opps signées de la période.
   *  Affiché en sub-info du CardDescription pour densifier sans ajouter
   *  une row dédiée (cf décision produit 2026-04-27, option A). */
  avgConversionDays: number | null;
}) {
  const accent = getAccentHex();
  const [view, setView] = useState<'bar' | 'pie'>('bar');
  // Filtre `no_follow_up` côté UI : compté au dénominateur (cf
  // overallConversionPct backend) pour ne pas sous-estimer le taux d'échec,
  // mais masqué visuellement (décision produit 2026-04-27 - en V1 cette
  // transition n'est pas atteignable via UI, afficher la barre serait
  // bruyant). `won`, `lost` etc. restent affichés.
  const rows = data.stages
    .filter((s) => s.stage !== 'no_follow_up')
    .map((s) => ({
      stage: STAGE_LABELS[s.stage] ?? s.stage,
      stageKey: s.stage,
      count: s.count,
      conversionFromPreviousPct: s.conversionFromPreviousPct,
    }));
  return (
    <Card className="flex flex-col lg:col-span-1">
      <div className="mb-1 flex items-start justify-between gap-3">
        <div>
          <CardTitle>Funnel pipeline</CardTitle>
          <CardDescription>
            Conversion {data.overallConversionPct} %
            {data.totalCreated > 0 && (
              <>
                {' '}
                <span className="text-text-muted/70">
                  ({data.stages.find((s) => s.stage === 'won')?.count ?? 0} signée(s) /{' '}
                  {data.totalCreated})
                </span>
              </>
            )}
          </CardDescription>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border-subtle bg-white">
          <button
            type="button"
            onClick={() => setView('bar')}
            aria-pressed={view === 'bar'}
            aria-label="Vue barres"
            className={cn(
              'flex h-7 w-7 items-center justify-center transition-colors',
              view === 'bar' ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-brand',
            )}
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setView('pie')}
            aria-pressed={view === 'pie'}
            aria-label="Vue camembert"
            className={cn(
              'flex h-7 w-7 items-center justify-center border-l border-border-subtle transition-colors',
              view === 'pie' ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-brand',
            )}
          >
            <PieChartIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-4 min-h-[320px] w-full flex-1">
        <ResponsiveContainer>
          {view === 'bar' ? (
            <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 16, top: 4 }}>
              <CartesianGrid {...GRID_STYLE} horizontal={false} vertical />
              {/* `allowDecimals={false}` : un compte d'opportunités est un
                  entier - évite les graduations 0.75 / 1.5 / 2.25 que Recharts
                  génère par défaut quand les valeurs sont très petites.
                  `width={150}` (vs 110 avant) : nécessaire pour les libellés
                  longs du pipeline V2 — "Attente éléments clients" (24 chars)
                  et "Attente devis Compagnie" (22 chars) qui sinon tronquent. */}
              <XAxis type="number" allowDecimals={false} {...AXIS_STYLE} />
              <YAxis type="category" dataKey="stage" {...AXIS_STYLE} width={150} interval={0} />
              <Tooltip cursor={{ fill: 'rgba(99, 91, 255, 0.04)' }} content={<ChartTooltip />} />
              <Bar
                dataKey="count"
                name="Opportunités"
                fill={accent}
                radius={[0, 999, 999, 0]}
                maxBarSize={14}
              >
                {rows.map((row) => (
                  <Cell
                    key={row.stageKey}
                    cursor="pointer"
                    onClick={() =>
                      openInPipeline({
                        stage: row.stageKey,
                        created_after: period.period_start,
                        created_before: period.period_end,
                      })
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <PieChart>
              <Pie
                data={rows}
                dataKey="count"
                nameKey="stage"
                cx="50%"
                cy="50%"
                innerRadius={72}
                outerRadius={90}
                paddingAngle={2}
                stroke="#FFFFFF"
                strokeWidth={2}
                cursor="pointer"
                onClick={(_, index) => {
                  const row = rows[index];
                  if (row?.stageKey)
                    openInPipeline({
                      stage: row.stageKey,
                      created_after: period.period_start,
                      created_before: period.period_end,
                    });
                }}
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
      {/* Footer : délai moyen de conversion. Posé en `mt-auto` pour le
          coller au bas de la card quand celle-ci s'étire à la hauteur de
          la sibling RevenueLineCard de la grille `lg:grid-cols-3`. */}
      <div className="mt-auto pt-4 border-t border-gray-100">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
            Délai moyen de conversion
          </span>
          <span className="text-sm font-semibold text-brand">
            {avgConversionDays !== null
              ? `${Math.round(avgConversionDays)} jours`
              : '— aucune signature'}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Évolution CA signé (cumulé) - courbe 12 mois (opps won par signed_at)
// ---------------------------------------------------------------------------

/** Mode d'affichage du chart Évolution CA — décision Q A 2026-04-30 :
 *  toggle Total/Commissions/F+H sur les 2 charts d'évolution CA. */
type RevenueMode = 'total' | 'commission' | 'fees_hono';

/** Toggle segmenté 3 boutons façon Stripe (segmented control compact) —
 *  réutilisé sur Dashboard + KPI Direction (cf KpiDirectionPage).
 *  Composant local pour éviter une primitive UI partagée tant qu'il n'est
 *  utilisé qu'à 2 endroits. */
function RevenueModeToggle({
  mode,
  onChange,
}: {
  mode: RevenueMode;
  onChange: (m: RevenueMode) => void;
}) {
  const opts: Array<{ value: RevenueMode; label: string; title: string }> = [
    { value: 'total', label: 'Total', title: 'CA total (commission + frais + honoraires)' },
    { value: 'commission', label: 'Com.', title: 'Commissions seules' },
    { value: 'fees_hono', label: 'F+H', title: 'Frais + honoraires uniquement' },
  ];
  return (
    <div className="inline-flex rounded-md border border-border-subtle bg-gray-50 p-[2px] text-[11px]">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          title={o.title}
          className={cn(
            'rounded-[5px] px-2 py-0.5 font-medium leading-tight transition-colors',
            mode === o.value ? 'bg-white text-brand shadow-sm' : 'text-text-muted hover:text-brand',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RevenueLineCard({
  data,
  granularity,
}: {
  data: DashboardResponse['revenueByMonth'];
  granularity: 'day' | 'week' | 'month';
}) {
  const accent = getAccentHex();
  const [mode, setMode] = useState<RevenueMode>('total');
  // Cumulatif : à chaque bucket (jour/semaine/mois) on affiche la somme depuis
  // le début de la période. Lecture immédiate de la trajectoire vs N-1.
  // Bascule 2026-04-30 (Q A + Q7) :
  //   - mode 'total'      : cumul Total CA (com + frais + honoraires) — défaut
  //   - mode 'commission' : cumul commission seule
  //   - mode 'fees_hono'  : cumul frais + honoraires uniquement
  // Le breakdown reste exposé via les 6 valeurs renvoyées par le backend
  // (commissionRevenue / feesAndHonorairesRevenue côté courant et N-1).
  const rows = useMemo(() => {
    // Accumulateurs cumulatifs (courant + N-1) pour les 3 séries.
    // Chaque bucket reçoit le cumul sélectionné selon `mode` côté `current`
    // et `previous`. Le breakdown commission/fees_hono est aussi cumulé
    // pour alimenter la stacked area en mode 'total' (Q7).
    let curTotal = 0;
    let curCom = 0;
    let curFH = 0;
    let prevTotal = 0;
    let prevCom = 0;
    let prevFH = 0;
    // Labels désambiguïsés (année suffixée sur les buckets en collision) pour
    // que Recharts résolve le bon point au survol/clic (cf tooltip janv N/N+1).
    const labels = disambiguateBucketLabels(
      data.map((d) => d.month),
      granularity,
    );
    return data.map((d, i) => {
      curTotal += d.revenue;
      curCom += d.commissionRevenue;
      curFH += d.feesAndHonorairesRevenue;
      prevTotal += d.previousYearRevenue;
      prevCom += d.previousCommissionRevenue;
      prevFH += d.previousFeesAndHonorairesRevenue;
      const current = mode === 'commission' ? curCom : mode === 'fees_hono' ? curFH : curTotal;
      const previous = mode === 'commission' ? prevCom : mode === 'fees_hono' ? prevFH : prevTotal;
      return {
        month: labels[i],
        monthIso: d.month,
        current: Math.round(current),
        previous: Math.round(previous),
        // Composantes (cumulées) — affichées comme stack en mode 'total'.
        currentCommission: Math.round(curCom),
        currentFeesAndHonoraires: Math.round(curFH),
      };
    });
  }, [data, granularity, mode]);

  function handleChartClick(state: unknown): void {
    const s = state as { activeLabel?: string } | null;
    const label = s?.activeLabel;
    if (!label) return;
    const row = rows.find((r) => r.month === label);
    if (!row) return;
    const range = bucketToIsoRange(row.monthIso, granularity);
    if (!range) return;
    // CA signé : on draille vers les opps `won` signées dans le bucket
    // (aligné sur le drill-down du chart « CA signé » du KPI Direction).
    openInPipeline({
      stage: 'won',
      signed_after: range.start,
      signed_before: range.end,
    });
  }
  const totals = useMemo(() => {
    // En cumulatif, le total = la dernière valeur de la série.
    const last = rows[rows.length - 1];
    const cur = last?.current ?? 0;
    const prev = last?.previous ?? 0;
    const deltaPct = prev === 0 ? null : Math.round(((cur - prev) / prev) * 100);
    return { cur, prev, deltaPct };
  }, [rows]);
  const allEmpty = totals.cur === 0 && totals.prev === 0;
  return (
    <Card className="flex flex-col lg:col-span-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CardTitle>Évolution CA signé (cumulé)</CardTitle>
          <CardDescription>
            CA signé cumulé mois après mois sur la période · comparé à N-1
          </CardDescription>
        </div>
        {/* Bloc droit du header (Stripe-like) : toggle compact + delta vs N-1
            empilés. Le toggle ne prend QUE la largeur de ses 3 boutons. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <RevenueModeToggle mode={mode} onChange={setMode} />
          {totals.deltaPct !== null && (
            <span
              className={cn(
                'text-xs font-medium',
                totals.deltaPct > 0
                  ? 'text-success-600'
                  : totals.deltaPct < 0
                    ? 'text-danger-600'
                    : 'text-text-muted',
              )}
            >
              {totals.deltaPct > 0 ? '+' : ''}
              {totals.deltaPct} % vs N-1
            </span>
          )}
        </div>
      </div>
      <div className="mt-4 min-h-[260px] flex-1 w-full">
        {allEmpty ? (
          <EmptyState
            title="Aucune donnée"
            description="Les mois s'afficheront au fur et à mesure."
          />
        ) : (
          <ResponsiveContainer>
            <AreaChart
              data={rows}
              margin={{ left: 0, right: 8, top: 4, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="month" {...AXIS_STYLE} />
              <YAxis
                {...AXIS_STYLE}
                tickFormatter={(v) => formatEuroCompact(Number(v))}
                width={56}
              />
              <Tooltip
                cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                content={<ChartTooltip formatValue={formatEuroCompact} />}
              />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
              />
              <Area
                type="monotone"
                name="N-1"
                dataKey="previous"
                stroke="#9CA3AF"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="transparent"
                dot={false}
                activeDot={{ r: 3, fill: '#9CA3AF' }}
              />
              {/* Mode 'total' (Q7) : on empile commission + F+H en stacked
                  area pour visualiser le breakdown. Mode 'commission' /
                  'fees_hono' : une seule série single area sur `current`. */}
              {mode === 'total' ? (
                <>
                  <Area
                    type="monotone"
                    name="Commissions"
                    dataKey="currentCommission"
                    stackId="1"
                    stroke={accent}
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: accent, strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                  <Area
                    type="monotone"
                    name="Frais + honoraires"
                    dataKey="currentFeesAndHonoraires"
                    stackId="1"
                    stroke="#A78BFA"
                    strokeWidth={2}
                    fill="#EDE9FE"
                    fillOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4, fill: '#A78BFA', strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                </>
              ) : (
                <Area
                  type="monotone"
                  name={mode === 'commission' ? 'Commissions' : 'Frais + honoraires'}
                  dataKey="current"
                  stroke={accent}
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: accent, strokeWidth: 2, stroke: '#FFFFFF' }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Répartition par type de contrat - camembert
// ---------------------------------------------------------------------------

function ContractTypeCard({
  data,
  period,
}: {
  data: DashboardResponse['contractTypeBreakdown'];
  period: { period_start: string; period_end: string };
}) {
  const rows = data.map((d) => ({
    name: d.code,
    value: d.count,
    label: d.label,
    contractTypeId: d.contractTypeId,
  }));
  return (
    <Card className="lg:col-span-1">
      <CardTitle>Types de contrat</CardTitle>
      <CardDescription>
        Répartition des opportunités - clic pour voir les opportunités du type
      </CardDescription>
      <div className="mt-4 h-[260px] w-full">
        {rows.length === 0 ? (
          <EmptyState title="Aucune donnée" />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={72}
                outerRadius={88}
                paddingAngle={2}
                stroke="#FFFFFF"
                strokeWidth={2}
                cursor="pointer"
                onClick={(_, index) => {
                  const row = rows[index];
                  if (row?.contractTypeId)
                    openInPipeline({
                      contract_type_id: row.contractTypeId,
                      created_after: period.period_start,
                      created_before: period.period_end,
                    });
                }}
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Activité par semaine - histogramme empilé
// ---------------------------------------------------------------------------

function ActivityCard({ data }: { data: DashboardResponse['activityByWeek'] }) {
  const accent = getAccentHex();
  // Chaque row garde la `weekISO` (lundi début de semaine côté backend) pour
  // pouvoir calculer les bornes `from`/`to` au clic (drill-down /activity).
  const rows = data.map((d) => ({
    weekISO: d.week,
    week: new Date(d.week).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
    Appels: d.call,
    RDV: d.meeting,
    Tâches: d.task,
    Emails: d.email,
    SMS: d.sms,
  }));

  // Clic sur une colonne → ouvre la page Activité filtrée sur cette semaine
  // (du lundi 00:00 au dimanche 23:59:59 en heure locale) avec les 3 types
  // stackés. Nouvel onglet pour préserver le contexte du dashboard.
  //
  // Calcul défensif des bornes : on repart du triplet (year, month, day) de
  // la weekISO pour construire des Date locales explicites. `new Date(iso)`
  // sur une string "YYYY-MM-DD" parse en UTC minuit - si le user est sur
  // +02:00, la journée locale démarre en réalité à 22:00 UTC la veille, ce
  // qui pouvait décaler le filtre côté serveur. En passant par Date.UTC
  // avec les composants extraits, on reste lisible et sans surprise TZ.
  function openInActivity(weekISO: string) {
    const [y, m, d] = weekISO.split('-').map(Number);
    if (!y || !m || !d) return;
    const from = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
    const to = new Date(Date.UTC(y, m - 1, d + 6, 23, 59, 59, 999));
    const params = new URLSearchParams({
      types: 'call,meeting,task,email,sms',
      from: from.toISOString(),
      to: to.toISOString(),
    });
    window.open(`/activity?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <Card className="lg:col-span-2">
      <CardTitle>Activité récente</CardTitle>
      <CardDescription>
        Activité par semaine sur la période · appels, RDV, tâches, emails, SMS. Cliquez sur une
        colonne pour voir le détail.
      </CardDescription>
      <div className="mt-4 h-[260px] w-full">
        {rows.length === 0 ? (
          <EmptyState title="Aucune activité" />
        ) : (
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ left: 0, right: 8, top: 4 }}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="week" {...AXIS_STYLE} />
              <YAxis {...AXIS_STYLE} width={32} />
              <Tooltip cursor={{ fill: 'rgba(99, 91, 255, 0.04)' }} content={<ChartTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
              />
              {/* Pattern Recharts Cell onClick : on capture la row
                  via une closure sur chaque <Cell> plutôt que de s'appuyer
                  sur `(_, index)` de <Bar onClick> qui était peu fiable
                  (bug observé : index de la Bar ≠ index dans rows en stacked).
                  Le <Cell key={i}> mémorise la weekISO exacte cliquée. */}
              <Bar
                dataKey="Appels"
                stackId="a"
                fill={accent}
                maxBarSize={14}
                radius={[0, 0, 0, 0]}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((row, i) => (
                  <Cell key={i} onClick={() => openInActivity(row.weekISO)} />
                ))}
              </Bar>
              <Bar
                dataKey="RDV"
                stackId="a"
                fill="#34D399"
                maxBarSize={14}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((row, i) => (
                  <Cell key={i} onClick={() => openInActivity(row.weekISO)} />
                ))}
              </Bar>
              <Bar
                dataKey="Tâches"
                stackId="a"
                fill="#F97316"
                maxBarSize={14}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((row, i) => (
                  <Cell key={i} onClick={() => openInActivity(row.weekISO)} />
                ))}
              </Bar>
              <Bar
                dataKey="Emails"
                stackId="a"
                fill="#60A5FA"
                maxBarSize={14}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((row, i) => (
                  <Cell key={i} onClick={() => openInActivity(row.weekISO)} />
                ))}
              </Bar>
              {/* SMS = barre du sommet de la pile → c'est elle qui porte le
                  radius arrondi `[4,4,0,0]` (sinon coins carrés en haut). */}
              <Bar
                dataKey="SMS"
                stackId="a"
                fill="#A78BFA"
                maxBarSize={14}
                radius={[4, 4, 0, 0]}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((row, i) => (
                  <Cell key={i} onClick={() => openInActivity(row.weekISO)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Alertes
// ---------------------------------------------------------------------------

function AlertsSection({
  data,
  onOpenOpportunity,
}: {
  data: DashboardResponse['alerts'];
  onOpenOpportunity: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Coins className="h-4 w-4 text-accent" />
          <CardTitle>Top commissions en cours</CardTitle>
        </div>
        <CardDescription>
          {data.topCommissions.length} opportunité{data.topCommissions.length > 1 ? 's' : ''} · tri
          par commission décroissante
        </CardDescription>
        <CommissionList items={data.topCommissions} onClick={onOpenOpportunity} />
      </Card>
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning-500" />
          <CardTitle>Devis en attente &gt; 7 j</CardTitle>
        </div>
        <CardDescription>
          {data.stalledQuotes.length} opportunité{data.stalledQuotes.length > 1 ? 's' : ''}
        </CardDescription>
        <AlertList
          items={data.stalledQuotes.map((q) => ({
            id: q.id,
            title: q.reference,
            subtitle: q.prospectName,
            when: q.stageChangedAt,
          }))}
          onClick={onOpenOpportunity}
        />
      </Card>
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4 text-warning-500" />
          <CardTitle>Opportunités stagnantes &gt; 30 j</CardTitle>
        </div>
        <CardDescription>
          {data.staleOpportunities.length} opportunité
          {data.staleOpportunities.length > 1 ? 's' : ''}
        </CardDescription>
        <AlertList
          items={data.staleOpportunities.map((o) => ({
            id: o.id,
            title: o.reference,
            subtitle: o.prospectName,
            when: o.lastActivityAt,
          }))}
          onClick={onOpenOpportunity}
        />
      </Card>
    </div>
  );
}

function CommissionList({
  items,
  onClick,
}: {
  items: Array<{
    id: string;
    reference: string;
    prospectName: string;
    commissionAmount: number;
    stage: string;
  }>;
  onClick: (id: string) => void;
}) {
  if (items.length === 0)
    return (
      <div className="py-6 text-center text-xs text-text-muted">Aucune opportunité en cours.</div>
    );
  return (
    <ul className="mt-3 max-h-[220px] divide-y divide-gray-100 overflow-y-auto">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onClick(item.id)}
            className="w-full py-2 text-left text-sm hover:bg-gray-50"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-mono text-xs text-text-muted">{item.reference}</span>
              <span className="shrink-0 text-[11px] font-semibold tabular-nums text-brand">
                {Math.round(item.commissionAmount).toLocaleString('fr-FR')} €
              </span>
            </div>
            <div className="truncate text-brand">{item.prospectName}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function AlertList({
  items,
  onClick,
}: {
  items: Array<{ id: string; title: string; subtitle: string; when: string | null }>;
  onClick: (id: string) => void;
}) {
  if (items.length === 0)
    return <div className="py-6 text-center text-xs text-text-muted">Rien à signaler.</div>;
  return (
    <ul className="mt-3 max-h-[220px] divide-y divide-gray-100 overflow-y-auto">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onClick(item.id)}
            className="w-full py-2 text-left text-sm hover:bg-gray-50"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-mono text-xs text-text-muted">{item.title}</span>
              {item.when && (
                <span className={cn('text-[11px]', 'text-warning-600')}>
                  {formatRelative(item.when)}
                </span>
              )}
            </div>
            <div className="truncate text-brand">{item.subtitle}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Skeleton className="h-[320px] lg:col-span-2" />
        <Skeleton className="h-[320px]" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Objectives widget - affiche les jauges des objectifs actifs visibles par
// l'utilisateur. Silencieux s'il n'y en a pas (pas de card vide).
// ---------------------------------------------------------------------------

function ObjectivesWidget() {
  const token = useAuthStore((s) => s.accessToken)!;
  const currentUser = useAuthStore((s) => s.user);
  const [items, setItems] = useState<
    Array<{
      obj: ObjectiveItem;
      progress: ObjectiveProgress;
      scope: 'personal' | 'team' | 'global';
    }>
  >([]);

  useEffect(() => {
    // Décision produit 2026-04-27 (option B) : le widget s'affiche pour TOUS
    // les rôles, y compris direction (admin/director/executive). La gestion
    // CRUD reste dans Config > Objectifs ; ici c'est juste un suivi de
    // progression visible au quotidien sur le dashboard. Si aucun objectif
    // n'est visible pour le user, la section reste masquée (pas de card
    // vide) - cf return null plus bas.
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await objectivesApi.list(token, { active_only: true });
        // Le collab/manager voit 3 catégories d'objectifs (décision produit
        // 2026-04-27) :
        //  1. Personnels  - userId === current user
        //  2. Équipe      - teamId === current user.teamId, userId null
        //  3. Globaux     - userId null ET teamId null (= « Tous »)
        // Le backend `listObjectives` filtre déjà sur le scope visible, mais
        // on re-tag côté client pour afficher le badge distinctif.
        const visible = list
          .map((o): { obj: ObjectiveItem; scope: 'personal' | 'team' | 'global' } | null => {
            if (o.user?.id === currentUser?.id) return { obj: o, scope: 'personal' };
            if (currentUser?.teamId && o.teamId === currentUser.teamId && o.userId === null)
              return { obj: o, scope: 'team' };
            if (o.userId === null && o.teamId === null) return { obj: o, scope: 'global' };
            return null;
          })
          .filter(
            (x): x is { obj: ObjectiveItem; scope: 'personal' | 'team' | 'global' } => x !== null,
          );

        const withProgress = await Promise.all(
          visible.map(async (v) => ({
            ...v,
            progress: await objectivesApi.progress(token, v.obj.id),
          })),
        );
        if (!cancelled) setItems(withProgress);
      } catch {
        /* silencieux - le dashboard reste utilisable */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, currentUser?.id, currentUser?.teamId]);

  if (items.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
        Mes objectifs
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map(({ obj, progress, scope }) => (
          <Card key={obj.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-brand">
                    {OBJECTIVE_TYPE_LABELS[obj.type]}
                  </span>
                  {scope === 'team' && <Badge tone="purple">Équipe</Badge>}
                  {scope === 'global' && <Badge tone="neutral">Global</Badge>}
                </div>
                <div className="mt-0.5 text-[11px] text-text-muted">
                  {OBJECTIVE_PERIOD_LABELS[obj.periodType]} ·{' '}
                  {progress.daysRemaining === 0
                    ? 'Dernier jour'
                    : `${progress.daysRemaining} j restants`}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Cible</div>
                <div className="text-sm font-semibold text-brand">
                  {progress.target.toLocaleString('fr-FR')}
                </div>
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  progress.progressPct >= 100
                    ? 'bg-success-600'
                    : progress.progressPct >= 50
                      ? 'bg-accent'
                      : 'bg-warning-500',
                )}
                style={{ width: `${Math.min(progress.progressPct, 100)}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-text-muted">
                Réalisé {progress.achieved.toLocaleString('fr-FR')}
              </span>
              <span className="font-semibold text-brand">{progress.progressPct} %</span>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
